import { useState } from 'react';
import { motion } from 'motion/react';
import { Factory, Plus, Pencil, Trash2 } from 'lucide-react';
import { useApp } from '@/store';
import { type Manufacturer, type Model } from '@/data/types';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollTable } from '@/components/layout';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type MfrForm = { name: string };
type ModelForm = {
  name: string;
  content: string;
  deviceType: string;
  agentType: string;
};
const blankMfr = (): MfrForm => ({ name: '' });
const blankModel = (): ModelForm => ({
  name: '',
  content: '',
  deviceType: '',
  agentType: '',
});

export function Manufacturers() {
  const t = useT();
  const mfrs = useApp(s => s.manufacturers);
  const types = useApp(s => s.deviceTypes);
  const upsertManufacturer = useApp(s => s.upsertManufacturer);
  const deleteManufacturer = useApp(s => s.deleteManufacturer);
  const upsertModel = useApp(s => s.upsertModel);
  const deleteModel = useApp(s => s.deleteModel);
  const toast = useApp(s => s.toast);

  const [selected, setSelected] = useState(mfrs[0]?.id ?? '');
  const active = mfrs.find(m => m.id === selected);

  const [mfrOpen, setMfrOpen] = useState(false);
  const [editingMfr, setEditingMfr] = useState<Manufacturer | null>(null);
  const [mfrForm, setMfrForm] = useState<MfrForm>(blankMfr());

  const [modelOpen, setModelOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [modelForm, setModelForm] = useState<ModelForm>(blankModel());

  // Manufacturer CRUD
  const openCreateMfr = () => {
    setEditingMfr(null);
    setMfrForm(blankMfr());
    setMfrOpen(true);
  };
  const openEditMfr = (m: Manufacturer) => {
    setEditingMfr(m);
    setMfrForm({ name: m.name });
    setMfrOpen(true);
  };
  const saveMfr = () => {
    if (!mfrForm.name.trim()) return;
    const id = editingMfr?.id ?? `MFR-${Date.now()}`;
    upsertManufacturer({
      id,
      name: mfrForm.name.trim(),
      models: editingMfr?.models ?? [],
    });
    if (!editingMfr) setSelected(id);
    toast(editingMfr ? t('common.save') : t('mfr.newMfr'));
    setMfrOpen(false);
  };
  const deleteMfr = (m: Manufacturer) => {
    if (!confirm(`${t('common.delete')} ${m.name}?`)) return;
    deleteManufacturer(m.id);
    setSelected(mfrs.filter(x => x.id !== m.id)[0]?.id ?? '');
    toast(t('common.delete'));
  };

  // Model CRUD
  const openCreateModel = () => {
    setEditingModel(null);
    setModelForm(blankModel());
    setModelOpen(true);
  };
  const openEditModel = (mdl: Model) => {
    setEditingModel(mdl);
    setModelForm({
      name: mdl.name,
      content: mdl.content,
      deviceType: mdl.deviceType,
      agentType: mdl.agentType,
    });
    setModelOpen(true);
  };
  const saveModel = () => {
    if (!active || !modelForm.name.trim() || !modelForm.deviceType) return;
    upsertModel(active.id, {
      id: editingModel?.id ?? `MDL-${Date.now()}`,
      mfrId: active.id,
      name: modelForm.name.trim(),
      content: modelForm.content.trim(),
      deviceType: modelForm.deviceType,
      agentType: modelForm.agentType.trim(),
    });
    toast(editingModel ? t('common.save') : t('mfr.addModel'));
    setModelOpen(false);
  };
  const handleDeleteModel = (mdl: Model) => {
    if (!active) return;
    if (!confirm(`${t('common.delete')} ${mdl.name}?`)) return;
    deleteModel(active.id, mdl.id);
    toast(t('common.delete'));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Factory className="h-6 w-6 text-primary" /> {t('mfr.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('mfr.subtitle')}</p>
        </div>
        <Button size="sm" onClick={openCreateMfr} data-focus="mfr-new">
          <Plus className="h-4 w-4" />
          {t('mfr.newMfr')}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('mfr.catalog')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {mfrs.map(m => (
              <div key={m.id} className="flex items-center gap-1">
                <button
                  onClick={() => setSelected(m.id)}
                  className={`flex-1 text-left px-3 py-2 rounded-md text-sm transition ${
                    m.id === selected
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'hover:bg-accent'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span>{m.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {m.models.length}
                    </Badge>
                  </div>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => openEditMfr(m)}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => deleteMfr(m)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">
                {active?.name ?? '—'} {t('mfr.models')}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={openCreateModel}
                disabled={!active}
              >
                <Plus className="h-3 w-3" />
                {t('mfr.addModel')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {active ? (
              <ScrollTable minWidth={500}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('ext.model')}</TableHead>
                      <TableHead>{t('ext.content')}</TableHead>
                      <TableHead>{t('table.deviceType')}</TableHead>
                      <TableHead>{t('ext.pressure')}</TableHead>
                      <TableHead className="w-20" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {active.models.map(mdl => {
                      const dt = types.find(x => x.id === mdl.deviceType);
                      return (
                        <TableRow key={mdl.id}>
                          <TableCell className="font-medium">
                            {mdl.name}
                          </TableCell>
                          <TableCell>{mdl.content}</TableCell>
                          <TableCell className="text-xs">
                            {dt?.nameDe}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {dt?.pressureClass ?? '—'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-11 w-11 sm:h-7 sm:w-7"
                                onClick={() => openEditModel(mdl)}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-11 w-11 sm:h-7 sm:w-7"
                                onClick={() => handleDeleteModel(mdl)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {active.models.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground py-6 text-sm"
                        >
                          {t('common.notFound')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollTable>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('common.notFound')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manufacturer dialog */}
      <Dialog open={mfrOpen} onOpenChange={setMfrOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingMfr ? t('common.edit') : t('mfr.newMfr')}
            </DialogTitle>
          </DialogHeader>
          <div>
            <Label>{t('mfr.mfrName')}</Label>
            <Input
              value={mfrForm.name}
              onChange={e => setMfrForm({ name: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMfrOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={saveMfr}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Model dialog */}
      <Dialog open={modelOpen} onOpenChange={setModelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingModel ? t('common.edit') : t('mfr.addModel')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>{t('ext.model')}</Label>
              <Input
                value={modelForm.name}
                onChange={e =>
                  setModelForm(f => ({ ...f, name: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('ext.content')}</Label>
              <Input
                value={modelForm.content}
                onChange={e =>
                  setModelForm(f => ({ ...f, content: e.target.value }))
                }
                placeholder="6 kg" // i18n-ignore
              />
            </div>
            <div>
              <Label>{t('mfr.agentType')}</Label>
              <Input
                value={modelForm.agentType}
                onChange={e =>
                  setModelForm(f => ({ ...f, agentType: e.target.value }))
                }
                placeholder="ABC" // i18n-ignore
              />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('table.deviceType')}</Label>
              <Select
                value={modelForm.deviceType}
                onValueChange={v =>
                  setModelForm(f => ({ ...f, deviceType: v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('table.deviceType')} />
                </SelectTrigger>
                <SelectContent>
                  {types.map(dt => (
                    <SelectItem key={dt.id} value={dt.id}>
                      {dt.nameDe}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModelOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={saveModel}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
